import type { IReactionDisposer } from 'mobx';
import { reaction } from 'mobx';
import type { IObjectViewModel } from './interfaces';
import type { ISelectionDependency } from './models/viewEntity/view.entity.ts';
import type { ViewsService } from './Views.service';
import type { IDisposable } from '@/engine/Utilities/IDisposable';

export class ViewItemsSelectionManager implements IDisposable {
  private _viewsService: ViewsService;

  private _selectionDependencies: Array<ISelectionDependency>;

  private _disposers: Array<IReactionDisposer> = [];

  private _viewItems: Array<IObjectViewModel> = [];

  public constructor(viewsService: ViewsService, selectionDependencies: Array<ISelectionDependency>) {
    this._viewsService = viewsService;
    this._selectionDependencies = selectionDependencies;

    const objects = this._viewsService.currentView?.getObjects();
    const measurements = this._viewsService.currentView?.getMeasurements();
    if (objects) {
      this._viewItems = this._viewItems.concat(objects);
    }
    if (measurements) {
      this._viewItems = this._viewItems.concat(measurements);
    }

    this._selectionDependencies.forEach((dependency: ISelectionDependency) => {
      const dependentItem: IObjectViewModel | undefined = this._geViewItemByDescriptor(dependency.item);
      if (!dependentItem) {
        throw new Error('Unknown dependent item descriptor');
      }

      const dependsOnItems: Array<IObjectViewModel> = [];
      for (const depOnDescriptor of dependency.dependsOn) {
        const depOnItem: IObjectViewModel | undefined = this._geViewItemByDescriptor(depOnDescriptor);
        if (depOnItem) {
          dependsOnItems.push(depOnItem);
        }
      }

      if (dependsOnItems.some((item) => item.isSelected)) {
        dependentItem.setSelection(true);
      }

      if (dependsOnItems.some((item) => item.isVisible)) {
        dependentItem.setVisibility(true);
      }

      this._disposers.push(
        reaction(
          () => dependentItem.isSelected,
          (selection) => {
            dependsOnItems.forEach((item) => item.setSelection(selection));
          }
        )
      );

      for (const depOnItem of dependsOnItems) {
        this._disposers.push(
          reaction(
            () => depOnItem.isSelected,
            (/*selection*/) => {
              const isSelected: boolean = dependsOnItems.some((item) => item.isSelected);
              dependentItem.setSelection(isSelected);
            }
          )
        );

        this._disposers.push(
          reaction(
            () => depOnItem.isVisible,
            (/*visibility*/) => {
              const isVisible: boolean = dependsOnItems.some((item) => item.isVisible);
              dependentItem.setVisibility(isVisible);
            }
          )
        );
      }
    });
  }

  private _geViewItemByDescriptor(descriptor: string): IObjectViewModel | undefined {
    return this._viewItems.find((item: IObjectViewModel): boolean => item.descriptor === descriptor);
  }

  public dispose(): void {
    this._disposers.forEach((disposer) => disposer());
  }
}
